import { useEffect, useState, useMemo } from "react";

import { Card, Divider, Grid, Icon, Menu } from "@mui/material";
import MenuItem from "@mui/material/MenuItem";

import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";

import MapboxMapComponent from "./components/MapboxMapComponent";
import config from "../../config";

import { fetchSuggestions } from "layouts/map/data/fetchSuggestions";
import { fetchAccidents } from "layouts/map/data/fetchAccidents";
import { fetchEvents } from "layouts/map/data/fetchEvents";
import { fetchInterventionCamera } from "layouts/map/data/fetchInterventionCamera";
import { fetchClusterInfo } from "./data/fetchClusterInfo";

import accidentIcon from "../../assets/images/Synchro/events-icons/incident.png";
import cameraIcon from "../../assets/images/Synchro/events-icons/camera-primary.png";
import constructionIcon from "../../assets/images/Synchro/events-icons/road-block.png";
import maintenanceIcon from "../../assets/images/Synchro/events-icons/road-block.png";
import tech from "../../assets/images/Synchro/events-icons/ai.png";

import { useRegionView } from "services/useRegionView";
import { useMaterialUIController } from "context";

const INTERVENTION_CATEGORIES = [
  {
    id: "icon-camera-layer-tech",
    icon: tech,
    name: "Τεχνολογική παρέμβαση",
    category: "Τεχνολογική παρέμβαση",
    useSourceIcon: true,
  },
  {
    id: "icon-camera-layer-project",
    icon: constructionIcon,
    name: "Έργο",
    category: "Έργο",
  },
  {
    id: "icon-camera-layer-maintenance",
    icon: maintenanceIcon,
    name: "Συντήρηση",
    category: "Συντήρηση",
  },
  {
    id: "icon-camera-layer-policing",
    icon: cameraIcon,
    name: "Αστυνόμευση",
    category: "Αστυνόμευση",
  },
];

function ClusterPage() {
  const [anchorEl, setAnchorEl] = useState(null);
  const [activeMenuId, setActiveMenuId] = useState(null);
  const [selectedCluster, setSelectedCluster] = useState(null);
  const [visibleCount, setVisibleCount] = useState(20);

  const [clusterData, setClusterData] = useState(null);
  const [suggestionData, setSuggestionData] = useState(null);
  const [accidentData, setAccidentData] = useState(null);
  const [interventionCameraData, setInterventionCameraData] = useState(null);
  const [eventData, setEventData] = useState(null);

  const { center, zoom } = useRegionView();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;

  useEffect(() => {
    const loadData = () => {
      fetchClusterInfo().then(setClusterData);
      fetchSuggestions().then(setSuggestionData);
      fetchAccidents().then(setAccidentData);
      fetchInterventionCamera().then(setInterventionCameraData);
      fetchEvents().then((data) => setEventData(data.data));
    };

    loadData();
    const interval = setInterval(loadData, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenMenu = (event, clusterId) => {
    setAnchorEl(event.currentTarget);
    setActiveMenuId(clusterId);
  };

  const handleCloseMenu = () => {
    setAnchorEl(null);
    setActiveMenuId(null);
  };

  const handleClusterClick = (cluster) => {
    setSelectedCluster(cluster);
    handleCloseMenu();
  };

  const layers = useMemo(() => {
    if (
      !clusterData ||
      !accidentData ||
      !interventionCameraData ||
      !suggestionData ||
      !eventData
    ) {
      return [];
    }

    const interventionFeatures = interventionCameraData?.features ?? [];
    const interventionChildLayers = INTERVENTION_CATEGORIES.map(
      ({ id, name, category, icon, useSourceIcon = false }) => ({
        id,
        parentId: "icon-camera-layer",
        name,
        visible: true,
        source: {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: interventionFeatures.filter(
              (feature) => feature?.properties?.category?.trim?.() === category
            ),
          },
        },
        layer: ["IconLayer"],
        layerStyle: {
          useSourceIcon,
          setIcon: icon,
          iconSize: 0.6,
        },
        canHover: true,
        canClick: true,
      })
    );

    return [

      // {
      //   id: "icon-events-layer",
      //   name: "Συμβάντα",
      //   visible: true,
      //   source: { type: "geojson", data: eventData },
      //   layer: ["IconLayer"],
      //   layerStyle: { useSourceIcon: true, iconSize: 0.9 },
      //   canHover: true,
      //   canClick: true,
      // },
      {
        id: "icon-accident-layer",
        name: "Ατυχήματα",
        visible: true,
        source: { type: "geojson", data: accidentData },
        layer: ["IconLayer"],
        layerStyle: { useSourceIcon: false, setIcon: accidentIcon, iconSize: 0.6 },
        canHover: true,
        canClick: true,
      },
      {
        id: "icon-camera-layer",
        isGroup: true,
        children: interventionChildLayers.map((layer) => layer.id),
        name: "Παρεμβάσεις",
        visible: true,
        source: { type: "geojson", data: interventionCameraData },
        layer: ["IconLayer"],
        layerStyle: { useSourceIcon: false, setIcon: cameraIcon, iconSize: 0.6 },
        canHover: true,
        canClick: true,
      },
      ...interventionChildLayers,
            {
        id: "point-suggestion-layer",
        name: "Υποδείξεις από φορείς",
        visible: false,
        source: { type: "geojson", data: suggestionData },
        layer: ["PointLayer"],
        layerStyle: { radius: 6, color: "#ffffb3", opacity: 0.2 },
        canHover: true,
        canClick: true,
      },
            {
        id: "point-cluster-layer",
        name: "Cluster",
        visible: false,
        source: { type: "geojson", data: clusterData },
        layer: ["FillLayer"],
        layerStyle: {
          color: darkMode ? "#cceeff" : "#1565c0",
          clickedOpacity: 0.4,
          hoverOpacity: 0.6,
          defaultOpacity: 0.3,
        },
        canHover: true,
        canClick: false,
      },
    ];
  }, [clusterData, accidentData, interventionCameraData, suggestionData, eventData, darkMode]);

  const mapUIConfig = useMemo(
    () => ({
      id: "layers-map",
      displayControlPanel: true,
      displayLayerList: true,
      display3DToggle: true,
      displayFilterPanel: true,
      displayLegend: true,
      displayOpacityControl: true,
      displayZoomControl: true,
      defaultMapStyle: "2d",
      map: {
        center,
        zoom,
        pitch: 0,
        bearing: 0,
        mapToken: config.MAPBOX_ACCESS_TOKEN_3D,
        style: {
          map2D: darkMode
            ? "mapbox://styles/mapbox/dark-v11"
            : "mapbox://styles/mapbox/streets-v12",
          map3D: config.MAP_3D,
        },
      },
    }),
    [center, zoom, darkMode]
  );

  if (!layers.length) {
    return (
      <DashboardLayout>
        <Card>
          <Grid container>
            <Grid item xs={12} md={3} pt={3} px={3}>
              <MDBox height="calc(100vh - 150px)" />
            </Grid>
            <Grid item xs={12} md={9}>
              <MDBox height="calc(100vh - 150px)" />
            </Grid>
          </Grid>
        </Card>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <Card>
        <Grid container>
          <Grid item xs={12} md={3} pt={3} px={3}>
            <MDBox
              height="calc(100vh - 150px)"
              overflow="auto"
              pr={1}
              sx={{
                scrollbarWidth: "thin",
                "&::-webkit-scrollbar": { width: "6px" },
                "&::-webkit-scrollbar-thumb": { backgroundColor: "#c1c1c1", borderRadius: "8px" },
              }}
            >
              {clusterData.features.slice(0, visibleCount).map((cluster) => (
                <MDBox key={cluster.properties.cluster_id} position="relative" mb={3}>
                  <MDBox component="li" display="flex" justifyContent="space-between" alignItems="flex-start" borderRadius="lg" p={1}>
                    <MDBox width="100%" display="flex" flexDirection="column">
                      <MDBox display="flex" justifyContent="space-between" alignItems="center" width="100%">
                        <MDTypography variant="body2" fontWeight="medium">
                          {cluster.properties.name}
                        </MDTypography>
                        <MDBox>
                          <MDButton variant="text" color="dark" onClick={(e) => handleOpenMenu(e, cluster.properties.cluster_id)}>
                            <Icon sx={{ color: "#fff" }}>more_vert</Icon>
                          </MDButton>
                          <Menu
                            anchorEl={anchorEl}
                            open={Boolean(anchorEl) && activeMenuId === cluster.properties.cluster_id}
                            onClose={handleCloseMenu}
                            keepMounted
                          >
                            <MenuItem onClick={() => handleClusterClick(cluster)}>
                              <Icon fontSize="small">visibility</Icon>&nbsp;Προβολή
                            </MenuItem>
                          </Menu>
                        </MDBox>
                      </MDBox>

                      <MDTypography variant="inherit" color="text" sx={{ fontSize: "16px !important" }}>
                        Σύνολο ατυχημάτων: {cluster.properties.accidents || 0}
                      </MDTypography>
                      <MDTypography variant="inherit" color="text" sx={{ fontSize: "16px !important" }}>
                        Σύνολο υποδείξεων: {cluster.properties.indications || 0}
                      </MDTypography>
                      <MDTypography variant="inherit" color="text" sx={{ fontSize: "16px !important" }}>
                        Σύνολο παρεμβάσεων: {cluster.properties.interventions || 0}
                      </MDTypography>
                      <MDTypography variant="caption" color="text" sx={{ fontSize: "14px !important" }}>
                        Τελευταία ενημέρωση: {cluster.properties.latestUpdate || ""}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                  <Divider />
                </MDBox>
              ))}

              <MDBox textAlign="center" mt={2}>
                {visibleCount < clusterData.features.length && (
                  <MDButton
                    variant="gradient"
                    color="info"
                    onClick={() => setVisibleCount((prev) => prev + 20)}
                    sx={{ textTransform: "none" }}
                  >
                    Περισσότερα
                  </MDButton>
                )}
              </MDBox>
            </MDBox>
          </Grid>

          <Grid item xs={12} md={9}>
            <MapboxMapComponent
              layers={layers}
              mapUIConfig={mapUIConfig}
              selectedCluster={selectedCluster}
              center={center}
              zoom={zoom}
            />
          </Grid>
        </Grid>
      </Card>
    </DashboardLayout>
  );
}

export default ClusterPage;
